import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { move, fileToUrl } from "../../lib/adminHelpers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { UploadField } from "./UploadField";
import { RowControls, DraftBadge } from "./RowControls";

const CATS = ["demo_reel", "challenge", "teaser", "audiobook", "narration", "voice", "music", "script"];
const CAT_LABEL = { demo_reel: "Demo Reels", challenge: "Challenge", teaser: "Teaser", audiobook: "Audiobook", narration: "Narration", voice: "Character Voice", music: "Music", script: "Sample Script" };
const empty = { category: "demo_reel", title: "", description: "", audio_path: "", video_path: "", cover_url: "", script_text: "", project_type: "", client: "", duration: "" };

const Fields = ({ f, set }) => (
  <div className="space-y-3">
    <div><Label className="text-xs text-[#A1A1AA]">Category</Label>
      <Select value={f.category} onValueChange={(v) => set({ ...f, category: v })}>
        <SelectTrigger className="bg-[#0B0C10] border-white/10 mt-1" data-testid="pf-category"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#12141A] border-white/10 text-white">{CATS.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[c]}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div><Label className="text-xs text-[#A1A1AA]">Title</Label>
      <Input value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} data-testid="pf-title" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    <div><Label className="text-xs text-[#A1A1AA]">Description</Label>
      <Input value={f.description} onChange={(e) => set({ ...f, description: e.target.value })} data-testid="pf-desc" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    <div className="grid grid-cols-3 gap-2">
      <div><Label className="text-xs text-[#A1A1AA]">Project type</Label>
        <Input value={f.project_type} onChange={(e) => set({ ...f, project_type: e.target.value })} placeholder="Commercial" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
      <div><Label className="text-xs text-[#A1A1AA]">Client</Label>
        <Input value={f.client} onChange={(e) => set({ ...f, client: e.target.value })} className="bg-[#0B0C10] border-white/10 mt-1" /></div>
      <div><Label className="text-xs text-[#A1A1AA]">Duration</Label>
        <Input value={f.duration} onChange={(e) => set({ ...f, duration: e.target.value })} placeholder="1:45" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    </div>
    <UploadField label="Cover image (optional)" accept="image/*" testid="pf-cover-upload" current={f.cover_url ? "Cover set" : ""} onUploaded={(p) => set({ ...f, cover_url: fileToUrl(p) })} />
    <div><Label className="text-xs text-[#A1A1AA]">Video URL (optional)</Label>
      <Input value={f.video_path} onChange={(e) => set({ ...f, video_path: e.target.value })} placeholder="https://... .mp4" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    {f.category === "script" && (
      <div><Label className="text-xs text-[#A1A1AA]">Script text</Label>
        <Textarea value={f.script_text} onChange={(e) => set({ ...f, script_text: e.target.value })} rows={3} className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    )}
    <UploadField label="Audio file" testid="pf-audio" current={f.audio_path ? "Audio set" : ""} onUploaded={(p) => set({ ...f, audio_path: p })} />
  </div>
);

export const PortfolioManager = ({ bump, onChange }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...empty });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/portfolio").then((r) => setItems(r.data)).catch(() => {});
  const done = () => { load(); onChange && onChange(); };
  useEffect(() => { load(); }, [bump]);

  const add = async () => {
    if (!form.title || (!form.audio_path && !form.video_path)) { toast.error("Title and an audio or video required"); return; }
    await api.post("/admin/portfolio", { ...form, status: "draft", order: items.filter((p) => p.category === form.category).length });
    toast.success("Added as Draft — click Go live to publish");
    setForm({ ...empty, category: form.category });
    done();
  };
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    await api.put(`/admin/portfolio/${editing.id}`, editing);
    toast.success("Saved"); setEditing(null); done();
  };
  const publish = async (it) => { await api.put(`/admin/portfolio/${it.id}`, { ...it, status: "published" }); toast.success("Published"); done(); };
  const del = async (id) => { await api.delete(`/admin/portfolio/${id}`); done(); };
  const toggleHide = async (it) => { await api.put(`/admin/portfolio/${it.id}`, { ...it, hidden: !it.hidden }); done(); };
  const reorder = async (group, i, dir) => {
    const next = move(group, i, dir);
    if (!next) return;
    await api.post("/admin/portfolio/reorder", { ids: next.map((x) => x.id) });
    load();
  };

  const groups = CATS.map((c) => [c, items.filter((p) => p.category === c)]).filter(([, arr]) => arr.length);

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="rounded-lg border border-white/10 bg-[#12141A] p-6 h-fit">
        <h3 className="font-head text-lg mb-4 flex items-center gap-2"><Plus size={18} className="text-[#C8A97E]" /> Add Sample</h3>
        <Fields f={form} set={setForm} />
        <Button onClick={add} data-testid="pf-add" className="w-full mt-4 bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Add Sample</Button>
      </div>

      <div className="space-y-6">
        {groups.length === 0 && <p className="text-[#A1A1AA]">No portfolio items yet.</p>}
        {groups.map(([cat, arr]) => (
          <div key={cat}>
            <div className="text-xs text-[#C8A97E] uppercase font-mono mb-2 tracking-wider">{CAT_LABEL[cat]}</div>
            <div className="space-y-2">
              {arr.map((p, i) => (
                <div key={p.id} data-testid={`pf-row-${p.id}`} className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${p.hidden ? "border-white/5 bg-[#0e0f14] opacity-60" : "border-white/10 bg-[#12141A]"}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">{p.title}<DraftBadge show={p.status === "draft"} />{p.hidden && <span className="text-[10px] uppercase text-[#E0A44F]">hidden</span>}</div>
                    {p.client && <div className="text-xs text-[#A1A1AA] truncate">{p.client}</div>}
                  </div>
                  <RowControls testid={`pf-${p.id}`} hidden={p.hidden} isFirst={i === 0} isLast={i === arr.length - 1}
                    isDraft={p.status === "draft"} onPublish={() => publish(p)}
                    onUp={() => reorder(arr, i, -1)} onDown={() => reorder(arr, i, 1)}
                    onToggleHide={() => toggleHide(p)} onEdit={() => setEditing({ ...empty, ...p })} onDelete={() => del(p.id)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#12141A] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="pf-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Edit Sample</DialogTitle></DialogHeader>
          {editing && <Fields f={editing} set={setEditing} />}
          <Button onClick={save} data-testid="pf-save" className="w-full mt-2 bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Save Changes</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
